import React, { useMemo, useRef } from 'react'
import { Skeleton } from 'antd'
import { useQueries } from '@tanstack/react-query'
import { CustomReport, CustomReportArgument } from '../../../../../redux/context/customReports/types'
import { customReportSectionOptions } from '../../../../../features/customReport/queries/section/customReportSectionOptions'
import usePageBreaks from './usePageBreaks'
import { usePrintSettings } from './PrintSettingsContext'
import PrintPaper from './PrintPaper'

interface PrintPreviewContentProps {
  report: CustomReport
  drawerReady: boolean
  companyId: string
  companyName?: string
  contractId?: number | null
  token?: string | null
  date?: string | null
  language?: string
  args?: CustomReportArgument[] | null
}

const PrintPreviewContent: React.FC<PrintPreviewContentProps> = ({
  report,
  drawerReady,
  companyId,
  companyName,
  contractId,
  token,
  date,
  language,
  args
}) => {
  const { settings, forcedBreaks, toggleForcedBreak, counterZoom, printableWidthPx } = usePrintSettings()

  const contentRef = useRef<HTMLDivElement>(null)

  const sections = report.sections || []

  const sectionQueries = useQueries({
    queries: sections.map(s =>
      customReportSectionOptions(
        companyId,
        s.reportId,
        s.id,
        { date: date ?? undefined, language, args: args ?? undefined },
        true
      )
    )
  })

  const allSettled = sectionQueries.every(q => q.data || q.isError)

  const evaluatedSectionMap = useMemo(
    () => new Map(sections.map((s, i) => [s.id, sectionQueries[i]?.data ?? null] as const)),
    [sections, sectionQueries]
  )

  const { breaks: breakPositions, ready: pageBreaksReady } = usePageBreaks({
    contentRef,
    enabled: drawerReady && allSettled,
    paperSize: settings.paperSize,
    orientation: settings.orientation,
    scale: settings.scale,
    margin: settings.margin,
    forcedBreakSectionIds: forcedBreaks
  })

  return (
    <>
      {!pageBreaksReady && <Skeleton active style={{ padding: 24 }} />}
      {drawerReady && (
        <PrintPaper
          mode="drawer"
          report={report}
          evaluatedSectionMap={evaluatedSectionMap}
          settings={settings}
          contentRef={contentRef}
          breakPositions={breakPositions}
          companyName={companyName}
          date={date}
          contractId={contractId ? String(contractId) : undefined}
          token={token ?? undefined}
          printableWidthPx={printableWidthPx}
          forcedBreaks={forcedBreaks}
          onToggleForcedBreak={toggleForcedBreak}
          counterZoom={counterZoom}
          style={{ visibility: pageBreaksReady ? 'visible' : 'hidden' }}
        />
      )}
    </>
  )
}

export default PrintPreviewContent
